import { useEffect, useState } from 'react';

import { type MapTheme } from './static-map-url';

const readDocumentTheme = (): MapTheme => {
	if (typeof document === 'undefined') return 'light';
	return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
};

/**
 * Mirrors the `data-theme` attribute `ThemeProvider` writes on `<html>` so the
 * map raster (light-v11 / dark-v11) flips with the rest of the page. Observed
 * through a MutationObserver rather than context so the lazy Leaflet chunk can
 * use it without threading providers.
 */
export const useDocumentTheme = (): MapTheme => {
	const [theme, setTheme] = useState<MapTheme>(readDocumentTheme);

	useEffect(() => {
		if (typeof MutationObserver === 'undefined') return;
		const root = document.documentElement;
		const observer = new MutationObserver(() => setTheme(readDocumentTheme()));
		observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
		setTheme(readDocumentTheme());
		return () => observer.disconnect();
	}, []);

	return theme;
};

export const prefersReducedMotion = (): boolean => {
	try {
		return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	} catch {
		return false;
	}
};
